import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { FiCheck, FiPaperclip, FiPlus, FiRotateCcw, FiX, FiArrowLeft } from 'react-icons/fi'

import './ClientTickets.css'
import PortalHeader from '../clientNavbar/PortalHeader'
import { useAuth } from '../../Auth'

import ClientTicketChat, { ChatMeta } from '../../team-portal/SupportTab/ClientTicketChat'
import { apiRequest, errorMessage, formatDate, timeAgo } from '../../team-portal/SupportTab/supportApi'
import {
    AUTO_CLOSED, CLIENT_ENVIRONMENTS, CLIENT_STATUSES, CLIENT_TICKET_TYPES, ClientTicket, SURVEY_LABELS,
} from '../../team-portal/SupportTab/clientTicketTypes'

const LIST_POLL_MS = 30000

type Filter = 'open' | 'resolved' | 'all'

const STATUS_SENTENCE: Record<string, string> = {
    'Submitted': 'We’ve received your ticket.',
    'Investigating': 'We’re looking into it.',
    'In Progress': 'We’re working on it.',
    'Implementing': 'We’re putting a fix in place.',
    'Resolved': 'This ticket is resolved.',
}

const STATUS_CLASS: Record<string, string> = {
    'Submitted': 'ct-s-submitted',
    'Investigating': 'ct-s-investigating',
    'In Progress': 'ct-s-progress',
    'Implementing': 'ct-s-implementing',
    'Resolved': 'ct-s-resolved',
}

/** How the resolved panel introduces each kind of closure */
const RESOLUTION_INTRO: Record<string, string> = {
    customer_confirmed: 'You confirmed the fix works.',
    verified_by_fnn: 'Our team confirmed the fix on our side.',
    closed_by_fnn: 'Our team closed this ticket.',
    auto_closed_no_input: 'We closed this ticket because we didn’t hear back.',
    auto_closed_no_validation: 'We closed this ticket because the fix wasn’t confirmed in time.',
}

// ─── Status track ─────────────────────────────────────────────────────────────

function StatusTrack({ ticket }: { ticket: ClientTicket }) {
    const current = CLIENT_STATUSES.indexOf(ticket.status)
    const resolved = ticket.status === 'Resolved'

    return (
        <div className={`ct-track ${resolved ? 'is-resolved' : ''}`}>
            <ol>
                {CLIENT_STATUSES.map((s, i) => {
                    const state = i < current || resolved ? 'done' : i === current ? 'current' : 'todo'
                    return (
                        <li key={s} className={`ct-step is-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
                            <span className="ct-step-dot">{state === 'done' && <FiCheck size={11} />}</span>
                            <span className="ct-step-label">{s}</span>
                        </li>
                    )
                })}
            </ol>
            <p className="ct-track-note">
                {ticket.awaiting_customer === 'input' && <><strong>Waiting for your reply.</strong> Answer our question in the conversation so we can keep going.</>}
                {ticket.awaiting_customer === 'validation' && <><strong>Waiting for you to test the fix.</strong> Let us know below whether it works.</>}
                {!ticket.awaiting_customer && STATUS_SENTENCE[ticket.status]}
            </p>
            {ticket.auto_close_at && (
                <p className="ct-track-deadline">
                    If we don’t hear back by {formatDate(ticket.auto_close_at)}, we’ll close this ticket. You can reopen it later if you still need help.
                </p>
            )}
        </div>
    )
}

// ─── Action panels ────────────────────────────────────────────────────────────

function ValidationPanel({ ticket, onDone }: { ticket: ClientTicket; onDone: (t: ClientTicket) => void }) {
    const [rejecting, setRejecting] = useState(false)
    const [comment, setComment] = useState('')
    const [busy, setBusy] = useState(false)

    const submit = async (accepted: boolean) => {
        if (!accepted && !comment.trim()) {
            toast.error('Tell us what still isn’t working.')
            return
        }
        setBusy(true)
        const { ok, data } = await apiRequest<ClientTicket>(`/portal/api/client-tickets/${ticket.ticket_id}/actions/`, {
            method: 'POST', json: { action: 'confirm', accepted, message: comment.trim() },
        })
        setBusy(false)
        if (!ok) { toast.error(errorMessage(data, 'Your answer wasn’t saved. Try again.')); return }
        toast.success(accepted ? 'Thanks! The ticket is now closed.' : 'Thanks. We’ve reopened work on it.')
        onDone(data)
    }

    return (
        <section className="ct-panel ct-panel-turn">
            <h3>Does the fix work for you?</h3>
            <p>Follow the testing steps in the conversation, then let us know. We’ll close the ticket when you confirm.</p>

            {rejecting ? (
                <div className="ct-panel-form">
                    <label htmlFor="ct-reject">What still isn’t working?</label>
                    <textarea id="ct-reject" rows={3} value={comment} onChange={e => setComment(e.target.value)}
                        placeholder="What you tried and what happened" />
                    <div className="ct-panel-actions">
                        <button className="ct-btn" onClick={() => setRejecting(false)} disabled={busy}>Cancel</button>
                        <button className="ct-btn ct-btn-primary" onClick={() => submit(false)} disabled={busy}>
                            {busy ? 'Sending…' : 'Send to the team'}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="ct-panel-actions">
                    <button className="ct-btn" onClick={() => setRejecting(true)} disabled={busy}>It’s not fixed</button>
                    <button className="ct-btn ct-btn-primary" onClick={() => submit(true)} disabled={busy}>
                        <FiCheck size={14} /> {busy ? 'Confirming…' : 'Yes, it works'}
                    </button>
                </div>
            )}
        </section>
    )
}

function SurveyPanel({ ticket, onDone }: { ticket: ClientTicket; onDone: (t: ClientTicket) => void }) {
    const [rating, setRating] = useState<number | null>(null)
    const [comment, setComment] = useState('')
    const [busy, setBusy] = useState(false)

    if (ticket.survey) {
        return (
            <div className="ct-survey is-done">
                <p>
                    <strong>Thanks for your feedback.</strong> You rated this {ticket.survey.rating} out of 5 ({ticket.survey.rating_label.toLowerCase()}).
                </p>
            </div>
        )
    }
    if (!ticket.survey_available) return null

    const submit = async () => {
        if (!rating) return
        setBusy(true)
        const { ok, data } = await apiRequest<ClientTicket>(`/portal/api/client-tickets/${ticket.ticket_id}/survey/`, {
            method: 'POST', json: { rating, comment: comment.trim() },
        })
        setBusy(false)
        if (!ok) { toast.error(errorMessage(data, 'Your feedback wasn’t sent. Try again.')); return }
        toast.success('Thanks for your feedback.')
        onDone(data)
    }

    return (
        <div className="ct-survey">
            <p id="ct-survey-q" className="ct-survey-q">How satisfied are you with how we handled this ticket?</p>
            <div className="ct-survey-scale" role="radiogroup" aria-labelledby="ct-survey-q">
                {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" role="radio" aria-checked={rating === n}
                        aria-label={`${n}: ${SURVEY_LABELS[n]}`}
                        className={rating === n ? 'active' : ''} onClick={() => setRating(n)}>
                        {n}
                    </button>
                ))}
            </div>
            <div className="ct-survey-ends" aria-hidden="true">
                <span>Very dissatisfied</span>
                <span>Very satisfied</span>
            </div>

            {rating && (
                <div className="ct-panel-form">
                    <label htmlFor="ct-survey-comment">
                        {rating >= 4 ? 'What went well? (optional)' : 'What could we have done better? (optional)'}
                    </label>
                    <textarea id="ct-survey-comment" rows={2} value={comment} maxLength={2000}
                        onChange={e => setComment(e.target.value)} />
                    <div className="ct-panel-actions ct-panel-actions-start">
                        <button className="ct-btn ct-btn-primary" onClick={submit} disabled={busy}>
                            {busy ? 'Sending…' : 'Send feedback'}
                        </button>
                        <span className="ct-muted">{SURVEY_LABELS[rating]}</span>
                    </div>
                </div>
            )}
        </div>
    )
}

function ResolvedPanel({ ticket, onDone }: { ticket: ClientTicket; onDone: (t: ClientTicket) => void }) {
    const [reopening, setReopening] = useState(false)
    const [reason, setReason] = useState('')
    const [busy, setBusy] = useState(false)

    const reopen = async () => {
        if (!reason.trim()) { toast.error('Tell us why you’re reopening the ticket.'); return }
        setBusy(true)
        const { ok, data } = await apiRequest<ClientTicket>(`/portal/api/client-tickets/${ticket.ticket_id}/actions/`, {
            method: 'POST', json: { action: 'reopen', message: reason.trim() },
        })
        setBusy(false)
        if (!ok) { toast.error(errorMessage(data, 'The ticket wasn’t reopened. Try again.')); return }
        toast.success('Ticket reopened.')
        setReopening(false)
        setReason('')
        onDone(data)
    }

    const autoClosed = AUTO_CLOSED.includes(ticket.resolution_type)
    const intro = ticket.resolution_type ? RESOLUTION_INTRO[ticket.resolution_type] : null

    return (
        <section className={`ct-panel ${autoClosed ? 'ct-panel-closed' : 'ct-panel-resolved'}`}>
            <h3>
                {autoClosed ? 'Closed' : 'Resolved'}
                {ticket.resolved_at && ` on ${formatDate(ticket.resolved_at)}`}
            </h3>
            {intro && <p>{intro}</p>}
            {ticket.resolution_notes
                ? <p className="ct-notes">{ticket.resolution_notes}</p>
                : !intro && <p>Our team marked this ticket as resolved.</p>}

            {!reopening && <SurveyPanel ticket={ticket} onDone={onDone} />}

            {ticket.can_reopen && !reopening && (
                <div className="ct-panel-actions ct-panel-actions-start">
                    <button className="ct-btn" onClick={() => setReopening(true)}>
                        <FiRotateCcw size={13} /> {autoClosed ? 'I still need help' : 'Reopen ticket'}
                    </button>
                    <span className="ct-muted">Available until {formatDate(ticket.reopen_deadline)}</span>
                </div>
            )}

            {reopening && (
                <div className="ct-panel-form">
                    <label htmlFor="ct-reopen">{autoClosed ? 'What do you still need help with?' : 'Why are you reopening it?'}</label>
                    <textarea id="ct-reopen" rows={3} value={reason} onChange={e => setReason(e.target.value)}
                        placeholder={autoClosed ? 'Answer our question, or tell us what’s still happening' : 'What came back, and when'} />
                    <div className="ct-panel-actions">
                        <button className="ct-btn" onClick={() => setReopening(false)} disabled={busy}>Cancel</button>
                        <button className="ct-btn ct-btn-primary" onClick={reopen} disabled={busy}>
                            {busy ? 'Reopening…' : 'Reopen ticket'}
                        </button>
                    </div>
                </div>
            )}
        </section>
    )
}

// ─── New ticket ───────────────────────────────────────────────────────────────

function NewTicketForm({ onCreated, onCancel }: { onCreated: (t: ClientTicket) => void; onCancel: () => void }) {
    const [form, setForm] = useState({ title: '', description: '', ticket_type: 'Issue', environment: 'Production' })
    const [files, setFiles] = useState<File[]>([])
    const [busy, setBusy] = useState(false)
    const fileRef = useRef<HTMLInputElement>(null)

    const set = (key: keyof typeof form) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
            setForm(prev => ({ ...prev, [key]: e.target.value }))

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.title.trim() || !form.description.trim()) {
            toast.error('Add a title and a description.')
            return
        }
        setBusy(true)
        const fd = new FormData()
        Object.entries(form).forEach(([k, v]) => fd.append(k, v))
        files.forEach(f => fd.append('attachments', f))
        const { ok, data } = await apiRequest<ClientTicket>('/portal/api/client-tickets/', { method: 'POST', form: fd })
        setBusy(false)
        if (!ok) { toast.error(errorMessage(data, 'Ticket not submitted. Try again.')); return }
        toast.success(`Ticket #${data.ticket_id} submitted.`)
        onCreated(data)
    }

    return (
        <form className="ct-new" onSubmit={submit}>
            <div className="ct-new-head">
                <h2>New ticket</h2>
                <p>Tell us what’s happening. You’ll get an email confirmation and can follow up in the ticket’s conversation.</p>
            </div>

            <div className="ct-grid-2">
                <label className="ct-field">
                    <span>Type</span>
                    <select value={form.ticket_type} onChange={set('ticket_type')}>
                        {CLIENT_TICKET_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                </label>
                <div className="ct-field">
                    <span id="ct-env-label">Environment</span>
                    <div className="ct-segment" role="radiogroup" aria-labelledby="ct-env-label">
                        {CLIENT_ENVIRONMENTS.map(env => (
                            <button key={env} type="button" role="radio" aria-checked={form.environment === env}
                                className={form.environment === env ? 'active' : ''}
                                onClick={() => setForm(prev => ({ ...prev, environment: env }))}>
                                {env}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <label className="ct-field">
                <span>Title</span>
                <input value={form.title} onChange={set('title')} maxLength={255}
                    placeholder="A short summary, e.g. “Detector map not loading”" />
            </label>

            <label className="ct-field">
                <span>Description</span>
                <textarea rows={6} value={form.description} onChange={set('description')}
                    placeholder="What happened, what you expected, and the steps to see it" />
            </label>

            <div className="ct-field">
                <span>Attachments <em>(optional, up to 15 MB each)</em></span>
                <div className="ct-drop" role="button" tabIndex={0}
                    onClick={() => fileRef.current?.click()}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click() } }}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]) }}>
                    <FiPaperclip size={14} /> Drop screenshots or logs here, or click to browse
                </div>
                {files.length > 0 && (
                    <div className="ct-drop-files">
                        {files.map((f, i) => (
                            <span key={i} className="ct-file-chip">
                                {f.name}
                                <button type="button" aria-label={`Remove ${f.name}`}
                                    onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>
                                    <FiX size={11} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                <input ref={fileRef} type="file" multiple hidden
                    onChange={e => { if (e.target.files) setFiles(prev => [...prev, ...Array.from(e.target.files!)]); e.target.value = '' }} />
            </div>

            <div className="ct-new-actions">
                <button type="button" className="ct-btn" onClick={onCancel} disabled={busy}>Cancel</button>
                <button type="submit" className="ct-btn ct-btn-primary" disabled={busy}>
                    {busy ? 'Submitting…' : 'Submit ticket'}
                </button>
            </div>
        </form>
    )
}

// ─── Ticket view ──────────────────────────────────────────────────────────────

function TicketView({ ticket, onUpdated, onBack }: {
    ticket: ClientTicket
    onUpdated: (t?: ClientTicket) => void
    onBack: () => void
}) {
    const [chatKey, setChatKey] = useState(0)

    const afterAction = (t: ClientTicket) => {
        onUpdated(t)
        setChatKey(k => k + 1)
    }

    // Status or "waiting on you" changed on the server → refresh the ticket
    const onMeta = (meta: ChatMeta) => {
        if (meta.status !== ticket.status || meta.awaiting_customer !== ticket.awaiting_customer) onUpdated()
    }

    return (
        <article className="ct-ticket">
            <button className="ct-back" onClick={onBack}><FiArrowLeft size={14} /> All tickets</button>

            <header className="ct-ticket-head">
                <span className="ct-ticket-id">Ticket #{ticket.ticket_id}</span>
                <h2>{ticket.title}</h2>
                <div className="ct-ticket-meta">
                    <span className="ct-tag">{ticket.ticket_type}</span>
                    <span className="ct-tag">{ticket.environment}</span>
                    <span className="ct-muted">Opened {formatDate(ticket.created_at)}</span>
                    {ticket.assigned_to_name && <span className="ct-muted">Handled by {ticket.assigned_to_name}</span>}
                </div>
            </header>

            <StatusTrack ticket={ticket} />

            {ticket.awaiting_customer === 'validation' && <ValidationPanel ticket={ticket} onDone={afterAction} />}
            {ticket.status === 'Resolved' && <ResolvedPanel ticket={ticket} onDone={afterAction} />}

            <div className="ct-ticket-body">
                <section className={`ct-conversation ${ticket.awaiting_customer === 'input' ? 'is-your-turn' : ''}`}>
                    <div className="ct-conversation-head">
                        <h3>Conversation</h3>
                        {ticket.awaiting_customer === 'input' && <span className="ct-turn-tag">Your reply is needed</span>}
                    </div>
                    <ClientTicketChat
                        ticketId={ticket.ticket_id}
                        perspective="client"
                        onMeta={onMeta}
                        refreshKey={chatKey}
                        placeholder={ticket.awaiting_customer === 'input' ? 'Answer the team’s question…' : 'Write a message to our team…'}
                    />
                </section>

                <aside className="ct-details">
                    <h3>Your original request</h3>
                    <p className="ct-description">{ticket.description}</p>
                    {ticket.attachments.length > 0 && (
                        <>
                            <h4>Attachments</h4>
                            <ul className="ct-attachments">
                                {ticket.attachments.map(a => (
                                    <li key={a.attachment_id}>
                                        <a href={a.file_url} target="_blank" rel="noopener noreferrer">
                                            <FiPaperclip size={12} /> {a.file_name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </aside>
            </div>
        </article>
    )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ClientTickets = () => {
    const { isAuthenticated } = useAuth()

    const [tickets, setTickets] = useState<ClientTicket[]>([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [filter, setFilter] = useState<Filter>('open')
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [creating, setCreating] = useState(false)

    const load = useCallback(async () => {
        const { ok, data } = await apiRequest<ClientTicket[]>('/portal/api/client-tickets/')
        if (ok) {
            setTickets(data)
            setLoadError(null)
        } else {
            setLoadError(errorMessage(data, 'Your tickets couldn’t be loaded.'))
        }
        setLoading(false)
    }, [])

    useEffect(() => {
        if (!isAuthenticated) return
        load()
        const timer = window.setInterval(() => { if (!document.hidden) load() }, LIST_POLL_MS)
        return () => window.clearInterval(timer)
    }, [isAuthenticated, load])

    const upsert = (t: ClientTicket) =>
        setTickets(prev => prev.some(p => p.ticket_id === t.ticket_id)
            ? prev.map(p => (p.ticket_id === t.ticket_id ? t : p))
            : [t, ...prev])

    const visible = useMemo(() => tickets.filter(t =>
        filter === 'all' ? true : filter === 'resolved' ? t.status === 'Resolved' : t.status !== 'Resolved'), [tickets, filter])

    const selected = tickets.find(t => t.ticket_id === selectedId) ?? null
    const needsYou = tickets.filter(t => t.awaiting_customer).length

    const select = (id: number) => {
        setCreating(false)
        setSelectedId(id)
        setTickets(prev => prev.map(t => (t.ticket_id === id ? { ...t, unread_count: 0 } : t)))
    }

    const showingDetail = creating || !!selected

    return (
        <div className="ct-root">
            <ToastContainer position="bottom-right" />
            <PortalHeader />

            <main className="ct-page">
                <div className="ct-top">
                    <div>
                        <h1>Support tickets</h1>
                        <p>Report a problem or ask a question, then follow it through to resolution with our team.</p>
                    </div>
                    <button className="ct-btn ct-btn-primary ct-btn-lg" onClick={() => { setCreating(true); setSelectedId(null) }}>
                        <FiPlus size={16} /> New ticket
                    </button>
                </div>

                <div className={`ct-layout ${showingDetail ? 'show-detail' : ''}`}>
                    <aside className="ct-list">
                        <div className="ct-filter" role="tablist">
                            {(['open', 'resolved', 'all'] as Filter[]).map(f => (
                                <button key={f} role="tab" aria-selected={filter === f}
                                    className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
                                    {f === 'open' ? 'Open' : f === 'resolved' ? 'Resolved' : 'All'}
                                </button>
                            ))}
                        </div>

                        {needsYou > 0 && (
                            <p className="ct-needs-you">{needsYou === 1 ? '1 ticket is' : `${needsYou} tickets are`} waiting for you.</p>
                        )}

                        <div className="ct-items">
                            {loading && <p className="ct-empty">Loading your tickets…</p>}
                            {!loading && loadError && (
                                <div className="ct-empty">
                                    <p>{loadError}</p>
                                    <button className="ct-btn" onClick={load}>Try again</button>
                                </div>
                            )}
                            {!loading && !loadError && visible.length === 0 && (
                                <p className="ct-empty">
                                    {filter === 'resolved' ? 'No resolved tickets yet.' : 'No open tickets. Use “New ticket” when you need help.'}
                                </p>
                            )}

                            {visible.map(t => (
                                <button key={t.ticket_id}
                                    className={`ct-item ${selectedId === t.ticket_id ? 'active' : ''} ${t.awaiting_customer ? 'is-waiting' : ''}`}
                                    onClick={() => select(t.ticket_id)}>
                                    <span className="ct-item-top">
                                        <span className="ct-item-id">#{t.ticket_id}</span>
                                        <span className={`ct-status ${STATUS_CLASS[t.status]}`}>
                                            {AUTO_CLOSED.includes(t.resolution_type) ? 'Closed' : t.status}
                                        </span>
                                    </span>
                                    <span className="ct-item-title">{t.title}</span>
                                    <span className="ct-item-bottom">
                                        {t.survey_available
                                            ? <span className="ct-item-rate">Rate how we did</span>
                                            : t.awaiting_customer
                                                ? <span className="ct-item-turn">
                                                    {t.awaiting_customer === 'validation' ? 'Test the fix' : 'Reply needed'}
                                                    {t.auto_close_at && ` by ${formatDate(t.auto_close_at)}`}
                                                </span>
                                                : <span className="ct-muted">Updated {timeAgo(t.updated_at)}</span>}
                                        {t.unread_count > 0 && (
                                            <span className="ct-unread" aria-label={`${t.unread_count} unread messages`}>{t.unread_count}</span>
                                        )}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </aside>

                    <section className="ct-main">
                        {creating && (
                            <NewTicketForm
                                onCancel={() => setCreating(false)}
                                onCreated={t => { upsert(t); setCreating(false); setSelectedId(t.ticket_id); setFilter('open') }}
                            />
                        )}

                        {!creating && selected && (
                            <TicketView
                                key={selected.ticket_id}
                                ticket={selected}
                                onUpdated={t => (t ? upsert(t) : load())}
                                onBack={() => setSelectedId(null)}
                            />
                        )}

                        {!creating && !selected && (
                            <div className="ct-placeholder">
                                <h2>Pick a ticket to see its progress</h2>
                                <p>Each ticket shows where it is in our process, and has a conversation with the team working on it.</p>
                                <button className="ct-btn ct-btn-primary" onClick={() => setCreating(true)}>
                                    <FiPlus size={14} /> New ticket
                                </button>
                            </div>
                        )}
                    </section>
                </div>
            </main>
        </div>
    )
}

export default ClientTickets